// ============================================
// Seed Test Orders
// ~45 realistic orders spread over 90 days
// for financial dashboard testing
// ============================================

import type { Order } from '../../types/order';
import { ORDER_PREFIX } from '../../config/brand';

// --- Helpers ---

function generateOrderId(idx: number): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = `${ORDER_PREFIX}-`;
  // Deterministic from index so re-seeding produces same IDs
  const seed = idx * 7919 + 1031;
  for (let i = 0; i < 8; i++) {
    id += chars[(seed * (i + 1) * 31) % chars.length];
  }
  return id;
}

function daysAgo(days: number, hour = 10): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hour, Math.floor(Math.random() * 60), 0, 0);
  return d.toISOString();
}

function hoursAfter(isoStr: string, hours: number): string {
  const d = new Date(isoStr);
  d.setHours(d.getHours() + hours);
  return d.toISOString();
}

function daysAfter(isoStr: string, days: number): string {
  const d = new Date(isoStr);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

// --- Test Customers ---

const CUSTOMERS = [
  { email: 'sarah.johnson@example.com', name: 'Sarah Johnson', state: 'FL' },
  { email: 'michael.chen@example.com', name: 'Michael Chen', state: 'CA' },
  { email: 'jessica.williams@example.com', name: 'Jessica Williams', state: 'NY' },
  { email: 'david.martinez@example.com', name: 'David Martinez', state: 'TX' },
  { email: 'emily.brown@example.com', name: 'Emily Brown', state: 'WA' },
  { email: 'james.wilson@example.com', name: 'James Wilson', state: 'FL' },
  { email: 'amanda.taylor@example.com', name: 'Amanda Taylor', state: 'CO' },
  { email: 'robert.anderson@example.com', name: 'Robert Anderson', state: 'AZ' },
  { email: 'jennifer.thomas@example.com', name: 'Jennifer Thomas', state: 'GA' },
  { email: 'christopher.lee@example.com', name: 'Christopher Lee', state: 'OR' },
  { email: 'lisa.garcia@example.com', name: 'Lisa Garcia', state: 'NV' },
  { email: 'daniel.moore@example.com', name: 'Daniel Moore', state: 'CT' },
  { email: 'ashley.jackson@example.com', name: 'Ashley Jackson', state: 'NC' },
  { email: 'matthew.white@example.com', name: 'Matthew White', state: 'OH' },
  { email: 'nicole.harris@example.com', name: 'Nicole Harris', state: 'PA' },
];

// --- Bundle Templates ---

interface BundleTemplate {
  bundleId: string;
  bundleName: string;
  bundlePrice: string;
  totalInCents: number;
  requiresProvider: boolean;
  supplements: { name: string; dosage: string; timing: string; requiresRx?: boolean }[];
}

const BUNDLES: BundleTemplate[] = [
  {
    bundleId: 'bnd-essential-foundation',
    bundleName: 'Essential Foundation',
    bundlePrice: '$149/mo',
    totalInCents: 14900,
    requiresProvider: false,
    supplements: [
      { name: 'Vitamin D3+K2', dosage: '5000 IU + 100mcg', timing: 'AM_ENERGY' },
      { name: 'Omega-3 Fish Oil', dosage: '2000mg EPA/DHA', timing: 'AM_ENERGY' },
      { name: 'Magnesium Glycinate', dosage: '400mg', timing: 'PM_RECOVERY' },
    ],
  },
  {
    bundleId: 'bnd-metabolic-reset',
    bundleName: 'Metabolic Reset',
    bundlePrice: '$149/mo',
    totalInCents: 14900,
    requiresProvider: false,
    supplements: [
      { name: 'Berberine', dosage: '500mg', timing: 'AM_ENERGY' },
      { name: 'Chromium Picolinate', dosage: '200mcg', timing: 'MIDDAY_ABSORPTION' },
      { name: 'Alpha Lipoic Acid', dosage: '600mg', timing: 'AM_ENERGY' },
    ],
  },
  {
    bundleId: 'bnd-thyroid-support',
    bundleName: 'Thyroid Support',
    bundlePrice: '$129/mo',
    totalInCents: 12900,
    requiresProvider: false,
    supplements: [
      { name: 'Selenium', dosage: '200mcg', timing: 'AM_ENERGY' },
      { name: 'Zinc Picolinate', dosage: '30mg', timing: 'PM_RECOVERY' },
      { name: 'Ashwagandha KSM-66', dosage: '600mg', timing: 'PM_RECOVERY' },
    ],
  },
  {
    bundleId: 'bnd-mens-optimization',
    bundleName: "Men's Optimization",
    bundlePrice: '$149/mo',
    totalInCents: 14900,
    requiresProvider: false,
    supplements: [
      { name: 'Tongkat Ali', dosage: '400mg', timing: 'AM_ENERGY' },
      { name: 'Boron', dosage: '10mg', timing: 'AM_ENERGY' },
      { name: 'DHEA', dosage: '25mg', timing: 'AM_ENERGY' },
      { name: 'Zinc Picolinate', dosage: '30mg', timing: 'PM_RECOVERY' },
    ],
  },
  {
    bundleId: 'bnd-womens-hormone',
    bundleName: "Women's Hormone Balance",
    bundlePrice: '$149/mo',
    totalInCents: 14900,
    requiresProvider: false,
    supplements: [
      { name: 'DIM', dosage: '200mg', timing: 'AM_ENERGY' },
      { name: 'Vitex (Chasteberry)', dosage: '400mg', timing: 'AM_ENERGY' },
      { name: 'Magnesium Glycinate', dosage: '400mg', timing: 'PM_RECOVERY' },
    ],
  },
  {
    bundleId: 'bnd-male-hrt',
    bundleName: 'Male HRT',
    bundlePrice: '$249/mo',
    totalInCents: 24900,
    requiresProvider: true,
    supplements: [
      { name: 'Testosterone Cypionate', dosage: '200mg/mL', timing: 'AM_ENERGY', requiresRx: true },
      { name: 'Anastrozole', dosage: '0.5mg', timing: 'MIDDAY_ABSORPTION', requiresRx: true },
      { name: 'DHEA', dosage: '25mg', timing: 'AM_ENERGY' },
    ],
  },
  {
    bundleId: 'bnd-female-hrt',
    bundleName: 'Female HRT',
    bundlePrice: '$249/mo',
    totalInCents: 24900,
    requiresProvider: true,
    supplements: [
      { name: 'Estradiol', dosage: '1mg', timing: 'AM_ENERGY', requiresRx: true },
      { name: 'Progesterone', dosage: '100mg', timing: 'PM_RECOVERY', requiresRx: true },
      { name: 'DHEA', dosage: '10mg', timing: 'AM_ENERGY' },
    ],
  },
  {
    bundleId: 'bnd-weight-loss-rx',
    bundleName: 'Weight Loss Rx',
    bundlePrice: '$399/mo',
    totalInCents: 39900,
    requiresProvider: true,
    supplements: [
      { name: 'Semaglutide', dosage: '0.5mg', timing: 'AM_ENERGY', requiresRx: true },
      { name: 'B12 Methylcobalamin', dosage: '5000mcg', timing: 'AM_ENERGY' },
      { name: 'Chromium Picolinate', dosage: '200mcg', timing: 'MIDDAY_ABSORPTION' },
    ],
  },
  {
    bundleId: 'bnd-longevity-core',
    bundleName: 'Longevity Core',
    bundlePrice: '$499/mo',
    totalInCents: 49900,
    requiresProvider: true,
    supplements: [
      { name: 'NAD+ Precursor (NMN)', dosage: '500mg', timing: 'AM_ENERGY' },
      { name: 'Resveratrol', dosage: '500mg', timing: 'AM_ENERGY' },
      { name: 'Rapamycin', dosage: '5mg weekly', timing: 'AM_ENERGY', requiresRx: true },
      { name: 'Metformin', dosage: '500mg', timing: 'PM_RECOVERY', requiresRx: true },
    ],
  },
  {
    bundleId: 'bnd-sleep-recovery',
    bundleName: 'Sleep + Recovery',
    bundlePrice: '$139/mo',
    totalInCents: 13900,
    requiresProvider: false,
    supplements: [
      { name: 'Magnesium Threonate', dosage: '2000mg', timing: 'PM_RECOVERY' },
      { name: 'L-Theanine', dosage: '200mg', timing: 'PM_RECOVERY' },
      { name: 'Apigenin', dosage: '50mg', timing: 'PM_RECOVERY' },
    ],
  },
];

// --- Order Definitions ---
// Each entry defines: daysAgo, customerIdx, bundleIdx, status

interface OrderDef {
  daysAgo: number;
  customerIdx: number;
  bundleIdx: number;
  status: 'pending' | 'paid' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
}

const ORDER_DEFS: OrderDef[] = [
  // 80-90 days ago — early orders, all delivered
  { daysAgo: 88, customerIdx: 0, bundleIdx: 0, status: 'delivered' },
  { daysAgo: 85, customerIdx: 1, bundleIdx: 3, status: 'delivered' },
  { daysAgo: 82, customerIdx: 2, bundleIdx: 4, status: 'delivered' },

  // 60-79 days ago — mix of delivered
  { daysAgo: 75, customerIdx: 3, bundleIdx: 5, status: 'delivered' },
  { daysAgo: 72, customerIdx: 4, bundleIdx: 2, status: 'delivered' },
  { daysAgo: 68, customerIdx: 5, bundleIdx: 1, status: 'delivered' },
  { daysAgo: 65, customerIdx: 0, bundleIdx: 8, status: 'delivered' }, // repeat customer
  { daysAgo: 62, customerIdx: 6, bundleIdx: 0, status: 'delivered' },
  { daysAgo: 60, customerIdx: 7, bundleIdx: 7, status: 'delivered' },

  // 40-59 days ago — growth phase
  { daysAgo: 55, customerIdx: 8, bundleIdx: 3, status: 'delivered' },
  { daysAgo: 52, customerIdx: 9, bundleIdx: 9, status: 'delivered' },
  { daysAgo: 50, customerIdx: 1, bundleIdx: 5, status: 'delivered' }, // repeat customer upgrades
  { daysAgo: 48, customerIdx: 10, bundleIdx: 0, status: 'delivered' },
  { daysAgo: 45, customerIdx: 11, bundleIdx: 4, status: 'delivered' },
  { daysAgo: 42, customerIdx: 12, bundleIdx: 1, status: 'delivered' },
  { daysAgo: 40, customerIdx: 3, bundleIdx: 7, status: 'cancelled' }, // one cancellation

  // 20-39 days ago — strong growth
  { daysAgo: 38, customerIdx: 13, bundleIdx: 6, status: 'delivered' },
  { daysAgo: 35, customerIdx: 14, bundleIdx: 0, status: 'delivered' },
  { daysAgo: 33, customerIdx: 0, bundleIdx: 0, status: 'shipped' },  // repeat customer re-order
  { daysAgo: 30, customerIdx: 5, bundleIdx: 8, status: 'shipped' },  // upgrade to longevity
  { daysAgo: 28, customerIdx: 2, bundleIdx: 5, status: 'shipped' },  // upgrade to HRT
  { daysAgo: 26, customerIdx: 6, bundleIdx: 3, status: 'shipped' },
  { daysAgo: 24, customerIdx: 8, bundleIdx: 9, status: 'shipped' },
  { daysAgo: 22, customerIdx: 11, bundleIdx: 7, status: 'processing' },
  { daysAgo: 20, customerIdx: 9, bundleIdx: 2, status: 'processing' },

  // 10-19 days ago — recent orders
  { daysAgo: 18, customerIdx: 4, bundleIdx: 6, status: 'processing' },
  { daysAgo: 16, customerIdx: 13, bundleIdx: 1, status: 'processing' },
  { daysAgo: 14, customerIdx: 7, bundleIdx: 0, status: 'paid' },
  { daysAgo: 12, customerIdx: 10, bundleIdx: 5, status: 'paid' },
  { daysAgo: 11, customerIdx: 14, bundleIdx: 8, status: 'paid' },
  { daysAgo: 10, customerIdx: 12, bundleIdx: 3, status: 'paid' },

  // 1-9 days ago — very recent
  { daysAgo: 8, customerIdx: 1, bundleIdx: 8, status: 'paid' },    // repeat customer longevity
  { daysAgo: 7, customerIdx: 3, bundleIdx: 0, status: 'paid' },
  { daysAgo: 6, customerIdx: 5, bundleIdx: 4, status: 'paid' },
  { daysAgo: 5, customerIdx: 8, bundleIdx: 7, status: 'paid' },
  { daysAgo: 4, customerIdx: 11, bundleIdx: 6, status: 'pending' },
  { daysAgo: 3, customerIdx: 6, bundleIdx: 9, status: 'pending' },
  { daysAgo: 2, customerIdx: 14, bundleIdx: 2, status: 'pending' },
  { daysAgo: 1, customerIdx: 9, bundleIdx: 5, status: 'pending' },
  { daysAgo: 0, customerIdx: 0, bundleIdx: 7, status: 'pending' },

  // Extra high-value orders
  { daysAgo: 44, customerIdx: 7, bundleIdx: 8, status: 'delivered' }, // Longevity $499
  { daysAgo: 25, customerIdx: 10, bundleIdx: 8, status: 'shipped' }, // Longevity $499
  { daysAgo: 15, customerIdx: 12, bundleIdx: 7, status: 'processing' }, // Weight Loss $399
  { daysAgo: 9, customerIdx: 4, bundleIdx: 8, status: 'paid' },    // Longevity $499
  { daysAgo: 36, customerIdx: 13, bundleIdx: 2, status: 'cancelled' }, // Another cancellation
];

// --- Build Orders ---

export function buildSeedOrders(): Order[] {
  return ORDER_DEFS.map((def, idx) => {
    const customer = CUSTOMERS[def.customerIdx];
    const bundle = BUNDLES[def.bundleIdx];
    const orderId = generateOrderId(idx);
    const createdAt = daysAgo(def.daysAgo, 8 + (idx % 12));
    const visitCents = bundle.requiresProvider ? 4900 : 0;
    const totalInCents = bundle.totalInCents + visitCents;

    // Build timestamps based on status
    let paidAt: string | undefined;
    let shippedAt: string | undefined;
    let deliveredAt: string | undefined;

    if (['paid', 'processing', 'shipped', 'delivered'].includes(def.status)) {
      paidAt = hoursAfter(createdAt, 1);
    }
    if (['shipped', 'delivered'].includes(def.status)) {
      shippedAt = daysAfter(createdAt, 2);
    }
    if (def.status === 'delivered') {
      deliveredAt = daysAfter(createdAt, 5);
    }

    const now = new Date().toISOString();

    const order: Order = {
      id: orderId,
      status: def.status,
      stripeSessionId: `cs_test_sim_${orderId}`,
      stripePaymentIntentId: paidAt ? `pi_test_sim_${orderId}` : undefined,
      customerEmail: customer.email,
      customerName: def.status !== 'pending' ? customer.name : undefined,
      shippingAddress: def.status !== 'pending' && def.status !== 'cancelled' ? {
        line1: `${100 + idx} Test Street`,
        city: 'Test City',
        state: customer.state,
        postal_code: `${10000 + idx}`,
        country: 'US',
      } : undefined,
      protocolSnapshot: {
        bundleId: bundle.bundleId,
        bundleName: bundle.bundleName,
        bundlePrice: bundle.bundlePrice,
        supplements: bundle.supplements.map(s => ({
          name: s.name,
          dosage: s.dosage,
          timing: s.timing,
          requiresRx: s.requiresRx,
        })),
        supplementCount: bundle.supplements.length,
        requiresProvider: bundle.requiresProvider,
      },
      lineItems: bundle.supplements.map(s => ({
        name: s.name,
        quantity: 1,
        priceInCents: 0,
      })),
      bundleId: bundle.bundleId,
      bundleName: bundle.bundleName,
      totalInCents,
      requiresProvider: bundle.requiresProvider,
      providerStatus: bundle.requiresProvider
        ? (def.status === 'pending' ? 'pending_review' : 'approved')
        : 'not_required',
      createdAt,
      updatedAt: deliveredAt || shippedAt || paidAt || now,
      paidAt,
      shippedAt,
      deliveredAt,
    };

    return order;
  });
}

export const SEED_TEST_ORDERS = buildSeedOrders();
